import { useState, useEffect } from "react";
import { toast } from "sonner";
import { productsService } from "../../services/products"; // We'll need to use customProducts service here actually, or a new admin method
import { authService } from "../../services/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { Package, Search, Filter, X } from "lucide-react";
import { getImageUrl } from "../../utils/imageUtils";
import { customProductsService } from "../../services/customProducts"; // Need to ensure this service supports admin params
import { storesService } from "../../services/stores"; // Need to fetch stores for filter

export default function AdminResellerProducts() {
    const [products, setProducts] = useState([]);
    const [stores, setStores] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filterStore, setFilterStore] = useState("all");
    const [searchQuery, setSearchQuery] = useState("");
    const [appliedSearch, setAppliedSearch] = useState("");

    // Pagination state
    const [page, setPage] = useState(1);
    const [limit] = useState(15);
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(0);

    useEffect(() => {
        fetchStores();
    }, []);

    useEffect(() => {
        setPage(1); // Reset page on filter changes
    }, [filterStore, appliedSearch]);

    useEffect(() => {
        fetchProducts();
    }, [filterStore, appliedSearch, page]);

    const fetchStores = async () => {
        try {
            // We need a service to get all stores for the dropdown. 
            // Assuming storesService.getAll() exists or we create it.
            // For now, let's assume we can fetch stores. If not, we might need to add that endpoint/service.
            // Checking storesService...
            const data = await storesService.getAll();
            setStores(data);
        } catch (error) {
            console.error("Failed to fetch stores", error);
            // toast.error("Failed to load stores for filtering"); 
        }
    };

    const fetchProducts = async () => {
        setLoading(true);
        try {
            const params = { page, limit };
            if (filterStore !== "all") params.store_id = filterStore;
            if (appliedSearch) params.search = appliedSearch;
            params.sort_by = 'store_name'; // Default sort

            // We need to use the method that hits /api/custom-products
            // But as an admin. The token will handle the role.
            // We just need to pass the params.
            const data = await customProductsService.getAll(params);

            if (data && data.pagination) {
                setProducts(data.data);
                setTotalPages(data.pagination.totalPages);
                setTotalItems(data.pagination.total);
            } else {
                const productsData = data && Array.isArray(data.data) ? data.data : (Array.isArray(data) ? data : []);
                setProducts(productsData);
                setTotalPages(1);
                setTotalItems(productsData.length);
            }
        } catch (error) {
            console.error("Failed to fetch reseller products", error);
            toast.error("Failed to fetch products");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="space-y-6 animate-fade-in" data-testid="admin-reseller-products">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                    <h2 className="text-2xl font-bold text-slate-900">
                        Products
                    </h2>
                    <p className="text-slate-500">View products from all resellers</p>
                </div>
                    <div className="flex items-center gap-3">
                    <div className="relative w-64">
                        <Search className="absolute left-2 top-2.5 h-4 w-4 text-slate-500" />
                        <Input
                            placeholder="Search products..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    setAppliedSearch(searchQuery);
                                    setPage(1);
                                }
                            }}
                            className="pl-8"
                        />
                    </div>
                    <Button
                        variant="outline"
                        size="sm"
                        className="h-10 px-3"
                        onClick={() => { setAppliedSearch(searchQuery); setPage(1); }}
                    >
                        <Search className="w-4 h-4" />
                    </Button>
                    {appliedSearch && (
                        <Button
                            variant="ghost"
                            size="sm"
                            className="h-10 px-2 text-slate-500"
                            onClick={() => { setSearchQuery(""); setAppliedSearch(""); setPage(1); }}
                        >
                            <X className="w-4 h-4 mr-1" />
                            Clear
                        </Button>
                    )}
                    <Select value={filterStore} onValueChange={setFilterStore}>
                        <SelectTrigger className="w-48">
                            <Filter className="w-4 h-4 mr-2 text-slate-500" />
                            <SelectValue placeholder="Filter by Store" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All Stores</SelectItem>
                            {stores.map((store) => (
                                <SelectItem key={store.store_id} value={store.store_id}>
                                    {store.business_name || store.store_name || "Unknown Store"}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            </div>

            <div className="card-elevated overflow-hidden">
                <Table>
                    <TableHeader>
                        <TableRow className="bg-slate-50">
                            <TableHead className="font-semibold">Product</TableHead>
                            <TableHead className="font-semibold">Store</TableHead>
                            <TableHead className="font-semibold">Price</TableHead>
                            <TableHead className="font-semibold">Stock</TableHead>
                            <TableHead className="font-semibold">Status</TableHead>
                            <TableHead className="font-semibold text-right">Created</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={6} className="h-32 text-center">
                                    <div className="flex items-center justify-center">
                                        <div className="w-6 h-6 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
                                    </div>
                                </TableCell>
                            </TableRow>
                        ) : !Array.isArray(products) || products.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                                    No products found.
                                </TableCell>
                            </TableRow>
                        ) : (
                            products.map((product) => (
                                <TableRow key={product.product_id || product._id} className="table-row">
                                    <TableCell>
                                        <div className="flex items-center gap-3">
                                            {product.image_url ? (
                                                <img
                                                    src={getImageUrl(product.image_url)}
                                                    alt={product.name}
                                                    className="w-10 h-10 rounded-lg object-cover"
                                                />
                                            ) : (
                                                <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                                                    <Package className="w-5 h-5 text-slate-400" />
                                                </div>
                                            )}
                                            <div>
                                                {product.is_addon && (
                                                    <span className="text-[10px] bg-indigo-50 text-indigo-600 px-1.5 py-0.5 rounded border border-indigo-100 mr-2">
                                                        Add-on
                                                    </span>
                                                )}
                                                <span className="font-medium text-slate-900">{product.name}</span>
                                                {product.sku && (
                                                    <p className="text-xs text-slate-500 font-mono">{product.sku}</p>
                                                )}
                                            </div>
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        <span className="font-medium text-slate-700">
                                            {product.store_name || "Unknown"}
                                        </span>
                                    </TableCell>
                                    <TableCell className="font-medium">
                                        ${product.base_price?.toFixed(2)}
                                        {product.display_price && product.display_price !== product.base_price && (
                                            <span className="text-xs text-slate-400 ml-1 line-through">${product.display_price}</span>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {product.inventory_quantity}
                                    </TableCell>
                                    <TableCell>
                                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${product.is_active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'}`}>
                                            {product.is_active ? 'Active' : 'Inactive'}
                                        </span>
                                    </TableCell>
                                    <TableCell className="text-right text-slate-500 text-sm">
                                        {new Date(product.created_at || product.createdAt).toLocaleDateString()}
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </div>

            {totalPages > 1 && (
                <div className="flex items-center justify-between px-2 py-4 border-t">
                    <div className="text-sm text-slate-500">
                        Showing <span className="font-medium">{(page - 1) * limit + 1}</span> to <span className="font-medium">{Math.min(page * limit, totalItems)}</span> of <span className="font-medium">{totalItems}</span> results
                    </div>
                    <div className="flex items-center gap-2">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setPage(p => Math.max(1, p - 1))}
                            disabled={page === 1}
                        >
                            Previous
                        </Button>
                        <div className="text-sm font-medium px-4">
                            Page {page} of {totalPages}
                        </div>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                            disabled={page >= totalPages}
                        >
                            Next
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
}
